<script lang="ts" setup>
/**
 * DashboardReasonConfirmDialog - Confirmation dialog for destructive admin
 * actions that require a mandatory reason (e.g. account deactivation,
 * mail deactivation, cart refund).
 *
 * Wraps DashboardConfirmDialog and adds a required reason textarea plus an
 * optional destructive-action warning note. Emits `confirm` with the trimmed
 * reason; the confirm button stays disabled until a reason is entered.
 */
import { ref, computed, watch } from 'vue'
import DashboardConfirmDialog from './DashboardConfirmDialog.vue'

const props = withDefaults(defineProps<{
  modelValue: boolean
  title: string
  subtitle?: string
  description?: string
  warning?: string
  icon?: string
  variant?: 'danger' | 'warning' | 'info' | 'primary'
  confirmText?: string
  confirmIcon?: string
  loading?: boolean
  reasonLabel: string
  reasonPlaceholder?: string
  maxLength?: number
  /**
   * Minimum trimmed length before confirm enables. Without it a caller whose
   * API enforces a minimum gets a 400 the customer cannot interpret, because
   * the dialog would happily submit a single character.
   */
  minLength?: number
  /** Keeps confirm disabled while fields the caller adds in the slot are incomplete. */
  confirmBlocked?: boolean
}>(), {
  variant: 'danger',
  loading: false,
  maxLength: 1024,
  minLength: 1
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', reason: string): void
  (e: 'cancel'): void
}>()

const reason = ref('')

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

// Reset reason each time the dialog opens
watch(() => props.modelValue, (open) => {
  if (open) reason.value = ''
})

const trimmedReason = computed(() => reason.value.trim())
const reasonLongEnough = computed(() => trimmedReason.value.length >= props.minLength)
const confirmable = computed(() => reasonLongEnough.value && !props.confirmBlocked)

function handleConfirm() {
  if (!confirmable.value || props.loading) return
  emit('confirm', trimmedReason.value)
}
</script>

<template>
  <DashboardConfirmDialog
    v-model="isOpen"
    :title="title"
    :subtitle="subtitle"
    :description="description"
    :icon="icon"
    :variant="variant"
    :confirm-text="confirmText"
    :confirm-icon="confirmIcon"
    :loading="loading"
    :confirm-disabled="!confirmable"
    persistent
    @confirm="handleConfirm"
    @cancel="emit('cancel')"
  >
    <slot />

    <div v-if="warning" class="warning-note" :class="`warning-note--${variant}`">
      <v-icon icon="mdi-alert" size="18" />
      <span>{{ warning }}</span>
    </div>

    <v-textarea
      v-model="reason"
      :label="reasonLabel"
      :placeholder="reasonPlaceholder"
      variant="outlined"
      density="compact"
      rows="3"
      :maxlength="maxLength"
      counter
      class="reason-textarea"
    />
  </DashboardConfirmDialog>
</template>

<style lang="scss" scoped>
.warning-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 0.85rem;
  line-height: 1.45;
  margin-bottom: 16px;

  &--danger {
    background: rgba(244, 67, 54, 0.08);
    border: 1px solid rgba(244, 67, 54, 0.2);
    color: #ef5350;
  }

  &--warning {
    background: rgba(255, 152, 0, 0.08);
    border: 1px solid rgba(255, 152, 0, 0.2);
    color: #ffb74d;
  }

  &--info,
  &--primary {
    background: rgba(33, 150, 243, 0.08);
    border: 1px solid rgba(33, 150, 243, 0.2);
    color: #64b5f6;
  }

  .v-icon {
    flex-shrink: 0;
    margin-top: 1px;
  }
}

.reason-textarea {
  margin-top: 4px;
}
</style>
